import { useEffect, useState } from 'react';

// Module-level registry of per-feed display-name overrides. The feed engine reads this
// synchronously to apply the user's custom names when returning feeds/posts. React drives the registry via setFeedNameOverrides
// whenever preferences.customFeedNames changes.

let overrides: Record<string, string> = {};
const listeners = new Set<() => void>();

export function setFeedNameOverrides(next: Record<string, string> | undefined): void {
  overrides = next ?? {};
  listeners.forEach(l => { try { l(); } catch (e) { console.error('Feed name override listener error:', e); } });
}

export function getFeedNameOverride(feedId: string): string | undefined {
  return overrides[feedId];
}

export function onFeedNameOverridesChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

// Returns a shallow-cloned object with shortTitle replaced by the user's custom
// name when one exists. The input is left untouched.
export function withFeedNameOverride<T extends { id: string; shortTitle?: string; title: string }>(feed: T): T {
  const custom = overrides[feed.id];
  return custom ? { ...feed, shortTitle: custom } : feed;
}

export function useFeedNameOverride(feedId: string | undefined): string | undefined {
  const [name, setName] = useState(() => feedId ? overrides[feedId] : undefined);
  useEffect(() => {
    if (!feedId) { setName(undefined); return; }
    setName(overrides[feedId]);
    return onFeedNameOverridesChange(() => setName(overrides[feedId]));
  }, [feedId]);
  return name;
}
